import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { phone } from 'phone';
import { useSelector } from 'react-redux';
import PhoneInput from 'react-phone-number-input';
import { signupSchema } from '../../../utils/formSchema';
import { dispatch } from '../../../redux/store';
import { isMvp } from '../../../utils/constants';
import { BigButtons } from '../../reusable/buttons/Buttons';
import 'react-phone-number-input/style.css';
import RoundedCheckbox from '../Custom/RoundedCheckbox';
import VerificationConfirmModal from './VerificationConfirmModal';
import { AuthApi, UserApi } from '../../../services';

import debounce from 'lodash/debounce';

const SignupForm = () => {
    const [phoneNumber, setPhoneNumber] = useState('');
    const [isErrorGettingPlayer, setError] = useState(false);
    const [playerDetails, setPlayerDetails] = useState();
    const [phoneError, setPhoneError] = useState('');
    const [visible, setVisible] = useState(false);
    const [emailEligible, setEmailEligible] = useState(true);
    const { type } = useLocation().state || { type: '' };

    const playerId = useSearchParams()[0].get('id');
    const checkValidity = phone(phoneNumber);
    const navigate = useNavigate();
    const modalRef = useRef();

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    const signupLoading = useSelector(
        ({ loading }) => loading.effects.auth.userSignup
    );

    const shouldAllowSignup = async email => {
        try {
            const respone = await AuthApi.checkForSignup({ email });
            if (respone.data.status === 1) {
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    };

    const checkEmailEligibility = useCallback(
        debounce(email => {
            if (email) {
                shouldAllowSignup(email).then(status => {
                    if (!status) {
                        setEmailEligible(false);
                    } else {
                        setEmailEligible(true);
                    }
                });
            }
        }, 500),
        []
    );

    const onSubmit = async values => {
        let newValues = {};

        newValues = {
            ...values,
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            phoneNumber,
            email: values.email.toLowerCase(),
            playerId: playerId || ''
        };

        if (phoneNumber && !checkValidity.isValid) {
            setPhoneError('Please add a valid phone number');
            return;
        }

        setPhoneError('');
        if (isMvp) {
            delete newValues.phoneNumber;
        }

        await dispatch.auth.userSignup({ user: newValues, navigate });
    };

    const handleOnBlur = () => {
        if (phoneNumber && !checkValidity.isValid) {
            setPhoneError('Please add a valid phone number');
        } else {
            setPhoneError('');
        }
    };

    const initialValues = {
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        userType: type,
        terms: false
    };

    const getPlayerInfo = async () => {
        try {
            const {
                data: { data }
            } = await UserApi.getGuestUserProfile(playerId);
            setPlayerDetails(data);
        } catch (error) {
            console.log('error', error);
            setError(true);
        }
    };

    const { values, errors, touched, handleBlur, handleChange, handleSubmit } =
        useFormik({
            initialValues,
            onSubmit,
            validationSchema: signupSchema
        });

    useEffect(() => {
        if (modalRef.current) {
            modalRef.current.click();
        }
        if (playerId) {
            getPlayerInfo();
        }
    }, [playerId]);

    return (
        <>
            {playerId && (
                <VerificationConfirmModal
                    isError={isErrorGettingPlayer}
                    playerName={
                        playerDetails
                            ? playerDetails?.lastName +
                              ' ' +
                              playerDetails?.firstName
                            : ''
                    }
                    ref={modalRef}
                />
            )}

            <form onSubmit={handleSubmit} className='mt-7'>
                <div className='grid grid-cols-2 gap-6'>
                    <div className='relative z-0 w-full mb-6 group'>
                        <input
                            type='text'
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.firstName}
                            name='firstName'
                            id='firstName'
                            className={`${
                                errors.firstName && touched.firstName
                                    ? 'border-b-2 border-red-400'
                                    : 'border-b-2 border-gray-300'
                            } block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent appearance-none dark:border-gray-600 dark:focus:border-black-800 focus:outline-none focus:ring-0 focus:border-gray-600 peer`}
                            placeholder=' '
                        />
                        <label
                            htmlFor='firstName'
                            className='peer-focus:font-medium absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-gray-600 peer-focus:dark:text-gray-100 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6'
                        >
                            First Name
                        </label>
                        <p className='text-red-500 text-xs italic py-1'>
                            {errors.firstName && touched.firstName
                                ? errors.firstName
                                : ''}
                        </p>
                    </div>
                    <div className='relative z-0 w-full mb-6 group'>
                        <input
                            type='text'
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.lastName}
                            name='lastName'
                            id='lastName'
                            className={`${
                                errors.lastName && touched.lastName
                                    ? 'border-b-2 border-red-400'
                                    : 'border-b-2 border-gray-300'
                            } block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent appearance-none dark:border-gray-600 dark:focus:border-black-800 focus:outline-none focus:ring-0 focus:border-gray-600 peer`}
                            placeholder=' '
                        />
                        <label
                            htmlFor='lastName'
                            className='peer-focus:font-medium absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-gray-600 peer-focus:dark:text-gray-100 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6'
                        >
                            Last Name
                        </label>
                        <p className='text-red-500 text-xs italic py-1'>
                            {errors.lastName && touched.lastName
                                ? errors.lastName
                                : ''}
                        </p>
                    </div>
                </div>

                <div className='relative z-0 w-full mb-6 group'>
                    <input
                        type='text'
                        onChange={e => {
                            if (emailRegex.test(e.target.value)) {
                                checkEmailEligibility(e.target.value);
                            } else {
                                setEmailEligible('');
                            }
                            handleChange(e);
                        }}
                        onBlur={handleBlur}
                        value={values.email}
                        name='email'
                        id='email'
                        className={`${
                            errors.email && touched.email
                                ? 'border-b-2 border-red-400'
                                : 'border-b-2 border-gray-300'
                        } block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent appearance-none dark:border-gray-600 dark:focus:border-black-800 focus:outline-none focus:ring-0 focus:border-gray-600 peer`}
                        placeholder=' '
                    />
                    <label
                        htmlFor='email'
                        className='peer-focus:font-medium absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-gray-600 peer-focus:dark:text-gray-100 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6'
                    >
                        Email
                    </label>
                    <p className='text-red-500 text-xs italic py-1'>
                        {errors.email && touched.email ? errors.email : ''}
                    </p>

                    {/* TO BE REMOVED  */}
                    {!emailEligible &&
                    typeof emailEligible === 'boolean' &&
                    emailRegex.test(values.email) ? (
                        <p className='font-poppins text-xs text-red-600'>
                            We are invite only at the moment. Visit{' '}
                            <a
                                className='text-red-700 underline'
                                target='_blank'
                                href='https://join.playerapp.co/'
                            >
                                join.playerapp.co
                            </a>{' '}
                            to join the waiting list.
                        </p>
                    ) : (
                        ''
                    )}
                </div>

                <div className='relative z-0 w-full mb-6 group'>
                    <img
                        onClick={() => setVisible(!visible)}
                        src={
                            visible
                                ? '/images/eye_show.png'
                                : '/images/eye_hide.png'
                        }
                        alt='visible'
                        className='absolute right-2 top-1/2 transform -translate-y-1/2 w-[20px] pb-3 '
                    />
                    <input
                        type={visible ? 'text' : 'password'}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.password}
                        name='password'
                        id='password'
                        className={`${
                            errors.password && touched.password
                                ? 'border-b-2 border-red-400'
                                : 'border-b-2 border-gray-300'
                        } block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent appearance-none dark:border-gray-600 dark:focus:border-black-800 focus:outline-none focus:ring-0 focus:border-gray-600 peer`}
                        placeholder=' '
                    />
                    <label
                        htmlFor='password'
                        className='peer-focus:font-medium absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-gray-600 peer-focus:dark:text-gray-100 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6'
                    >
                        Password
                    </label>
                    <p className='text-red-500 text-xs italic py-1'>
                        {errors.password && touched.password
                            ? errors.password
                            : ''}
                    </p>
                </div>
                {!isMvp && (
                    <div className='grid md:grid-cols-2 md:gap-6'>
                        <div className='relative z-0 w-full mb-6 group'>
                            <PhoneInput
                                international
                                countryCallingCodeEditable={false}
                                defaultCountry='GB'
                                onChange={e => setPhoneNumber(e)}
                                className={`${
                                    phoneError
                                        ? 'border-b-2 border-red-400'
                                        : 'border-b-2 border-gray-300'
                                } block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent appearance-none dark:border-gray-600 dark:focus:border-black-800 focus:outline-none focus:ring-0 focus:border-gray-600 peer`}
                                id='phoneNumber'
                                onBlur={handleOnBlur}
                                value={phoneNumber}
                                type={'string'}
                                placeholder='Enter Phone Number (optional)'
                            />
                            <label
                                htmlFor='phone'
                                className='peer-focus:font-medium absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-gray-600 peer-focus:dark:text-gray-100 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6'
                            >
                                Phone Number (optional)
                            </label>
                            <p className='text-red-500 text-xs italic py-1'>
                                {phoneError || ''}
                            </p>
                        </div>
                    </div>
                )}
                <div className='grid grid-cols-2'>
                    <div className='form-control'>
                        <label className='label justify-normal cursor-pointer'>
                            <input
                                value={'PLAYER'}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                type='radio'
                                name='userType'
                                className='radio border-2 border-gray-700 focus:outline-none checked-black-bg'
                                checked={values.userType === 'PLAYER'}
                            />
                            <span className='label-text ml-5'>
                                I’m a player
                            </span>
                        </label>
                    </div>
                    <div className='form-control'>
                        <label className='label justify-normal cursor-pointer'>
                            <input
                                value={'NON_PLAYER'}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                type='radio'
                                name='userType'
                                className='radio border-2 border-gray-700 focus:outline-none checked-black-bg'
                                checked={values.userType === 'NON_PLAYER'}
                            />
                            <span className='label-text ml-5'>
                                I’m not a player
                            </span>
                        </label>
                    </div>

                    <p className='text-red-500 text-xs italic py-1'>
                        {errors.userType && touched.userType
                            ? errors.userType
                            : ''}
                    </p>
                </div>
                <div className='pr-3 my-4'>
                    <RoundedCheckbox
                        id='terms'
                        name='terms'
                        onChange={handleChange}
                        value={values.terms}
                        htmlLabel={
                            "<span> I agree with the PLAYER <a target='_blank' href='https://bird-greyhound-smlz.squarespace.com/tcs' className='text-blue-500'>Terms & Conditions</a> and <a target='_blank' href='https://bird-greyhound-smlz.squarespace.com/privacypolicy' className='text-blue-500'>Privacy Policy</a></span>"
                        }
                        errorLabel={
                            errors.terms && touched.terms ? errors.terms : ''
                        }
                    />
                </div>
                <BigButtons
                    isDisabled={!emailEligible}
                    className={!emailEligible ? 'bg-slate-700' : ''}
                    isLoading={signupLoading}
                    black
                    label='SIGN UP'
                />
            </form>
        </>
    );
};

export default SignupForm;
